import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useGetAppUrlsQuery, useUpdateAppUrlThemeMutation } from "../services/user/userApi";
import { SITE_THEMES, THEME_KEYS } from "../config/siteThemes";
import { DOMAIN_THEMES, getBaseDomain } from "../utils/domainConfig";

// Site Theme — har domain (AppUrl) ke liye client site ki theme chuno, asli screenshots ke preview ke saath.
// Theme backend ke AppUrl.theme me save hoti hai; client site startup pe /api/appurls/theme?domain= se padhti hai.
// Backend me field na ho to code ki DOMAIN_THEMES list chalti hai (docs: sky99/docs/SITE_THEME_BACKEND.md).

const PhoneShot = ({ src, label }) => (
  <div className="flex flex-col items-center gap-2">
    <div className="rounded-[22px] border-[5px] border-[#1f2937] bg-[#1f2937] shadow-lg overflow-hidden" style={{ width: 175 }}>
      <img src={src} alt={label} className="block w-full" style={{ aspectRatio: "390 / 844", objectFit: "cover", objectPosition: "top" }} />
    </div>
    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</span>
  </div>
);

const ThemeBadge = ({ themeKey, fromCode }) => {
  const t = SITE_THEMES[themeKey] || SITE_THEMES.default;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.swatches[1] }} />
      {t.label}
      {fromCode && <span className="font-normal text-gray-400">(code)</span>}
    </span>
  );
};

const SiteThemePage = () => {
  const navigate = useNavigate();
  // Level bilkul Sidebar jaisa (user?.userTypeInfo || 7) — jisko menu dikhe use page bhi; asli
  // rok backend pe (PUT /api/appurls admin-only). Sirf sky99.co ke admin pe.
  const user = useSelector((state) => state.auth.user);
  const onSkyAdmin = getBaseDomain() === "sky99.co";
  const isAdmin = (user?.userTypeInfo || 7) === 7 && onSkyAdmin;
  useEffect(() => { if (!onSkyAdmin) navigate("/dashboard"); }, [onSkyAdmin, navigate]);

  const { data, isLoading, isError, refetch } = useGetAppUrlsQuery(undefined, { skip: !isAdmin });
  const [updateTheme, { isLoading: saving }] = useUpdateAppUrlThemeMutation();

  const domains = useMemo(() => {
    const list = data?.data || (Array.isArray(data) ? data : []);
    return list.map((a) => {
      const url = String(a.url || "").toLowerCase();
      const backendTheme = SITE_THEMES[a.theme] ? a.theme : null;
      return { id: a._id, name: a.name, url, isActive: a.isActive !== false, theme: backendTheme || DOMAIN_THEMES[url] || "default", fromCode: !backendTheme };
    });
  }, [data]);

  const [selectedId, setSelectedId] = useState(null);
  const selected = domains.find((d) => d.id === selectedId) || domains[0];
  // har domain ka chuna hua (abhi save nahi hua) theme — na chuna ho to current
  const [drafts, setDrafts] = useState({});
  const draft = (selected && drafts[selected.id]) || selected?.theme || "default";
  const setDraft = (k) => selected && setDrafts((prev) => ({ ...prev, [selected.id]: k }));

  const draftTheme = SITE_THEMES[draft] || SITE_THEMES.default;
  const changed = selected && draft && draft !== selected.theme;

  const onSave = async () => {
    if (!selected?.id) return;
    try {
      const res = await updateTheme({ id: selected.id, theme: draft }).unwrap();
      const saved = res?.data?.theme ?? res?.theme;
      if (saved === draft) {
        setDrafts((prev) => { const n = { ...prev }; delete n[selected.id]; return n; });
        toast.success(`${selected.url} pe ab "${draftTheme.label}" theme`);
      } else {
        toast.warn("Backend ne theme save nahi ki — AppUrl me `theme` field chahiye (docs/SITE_THEME_BACKEND.md)");
      }
      refetch();
    } catch (e) {
      toast.error(e?.data?.message || "Theme save nahi hui");
    }
  };

  if (!isAdmin) {
    return <div className="p-6 text-center text-gray-500">Site Theme sirf sky99 ke Admin ke liye hai.</div>;
  }

  return (
    <div className="p-3 sm:p-4">
      <div className="bg-white rounded shadow border border-gray-200">
        <div className="bg-[#3e525f] px-5 py-3 flex items-center justify-between">
          <span className="text-xl text-white font-normal">Site Theme</span>
        </div>
        <p className="px-5 pt-3 text-sm text-gray-500">
          Domain chuno → theme chuno → preview me turant dekho kaisi dikhegi → Save.
          Badlaav client site pe agli baar page khulne pe dikhega.
        </p>

        {isLoading && <div className="p-6 text-center text-gray-500">Loading domains…</div>}
        {isError && (
          <div className="p-6 text-center text-red-600">
            Domains load nahi hue. <button className="underline" onClick={refetch}>Dobara try karo</button>
          </div>
        )}

        {!isLoading && !isError && (
          // 3 column: domains | themes (compact) | preview (sticky) — sab ek saath ek screen pe
          <div className="grid grid-cols-1 lg:grid-cols-[240px_260px_1fr] gap-4 p-4 items-start">
            {/* Domains */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-500 bg-gray-50 border-b border-gray-200">1 · Domain</div>
              {domains.length === 0 && <div className="p-4 text-sm text-gray-500">Koi domain nahi mila</div>}
              {domains.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setSelectedId(d.id)}
                  className={`w-full text-left px-4 py-2.5 border-b border-gray-100 transition-colors ${selected?.id === d.id ? "bg-gray-100" : "hover:bg-gray-50"}`}
                >
                  <div className="font-semibold text-sm text-gray-800 truncate">{d.url || <span className="text-gray-400 italic">(url khaali)</span>}</div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <span className="text-[11px] text-gray-400 truncate">{d.name}{!d.isActive && " · inactive"}</span>
                    <ThemeBadge themeKey={d.theme} fromCode={d.fromCode} />
                  </div>
                </button>
              ))}
            </div>

            {/* Themes — compact list */}
            {selected && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-500 bg-gray-50 border-b border-gray-200">2 · Theme</div>
                <div className="p-2 flex flex-col gap-1.5">
                  {THEME_KEYS.map((k) => {
                    const t = SITE_THEMES[k];
                    const active = draft === k;
                    const current = selected.theme === k;
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setDraft(k)}
                        title={t.description}
                        className={`text-left rounded-lg border-2 px-2.5 py-2 flex items-center gap-2.5 transition-all ${active ? "shadow-sm bg-white" : "border-transparent hover:bg-gray-50"}`}
                        style={active ? { borderColor: t.swatches[1] } : undefined}
                      >
                        <span className="flex h-9 w-12 rounded-md overflow-hidden border border-black/10 shrink-0">
                          {t.swatches.map((c) => <span key={c} className="flex-1" style={{ background: c }} />)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold text-gray-800 truncate">{t.label}</span>
                          <span className="block text-[11px] text-gray-400 truncate">{t.subtitle}</span>
                        </span>
                        {current && <span className="text-[9px] font-bold uppercase text-green-600 shrink-0">Abhi</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Preview — sticky, Save upar */}
            {selected && (
              <div className="min-w-0 lg:sticky lg:top-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500">3 · Preview</div>
                    <div className="text-base font-semibold text-gray-800 truncate">
                      {selected.url || selected.name} pe <span style={{ color: draftTheme.swatches[1] }}>{draftTheme.label}</span> aisi dikhegi
                    </div>
                    <div className="text-[11px] text-gray-400">Client site ke asli screenshots (mobile). Logo aur naam domain ke hisaab se badlenge.</div>
                  </div>
                  <button
                    type="button"
                    disabled={!changed || saving}
                    onClick={onSave}
                    className="bg-blue-500 text-white px-5 py-2 rounded disabled:opacity-60 shrink-0"
                  >
                    {saving ? "Saving…" : changed ? `Save — ${draftTheme.label}` : "Saved"}
                  </button>
                </div>

                <div className="flex flex-wrap xl:flex-nowrap items-end justify-center gap-4">
                  {changed && (
                    <div className="flex items-end gap-3">
                      <div className="flex flex-col items-center gap-1.5 opacity-80">
                        <div className="rounded-[14px] border-[4px] border-[#1f2937] bg-[#1f2937] overflow-hidden" style={{ width: 84 }}>
                          <img src={(SITE_THEMES[selected.theme] || SITE_THEMES.default).previews.home} alt="abhi" className="block w-full" style={{ aspectRatio: "390 / 844", objectFit: "cover", objectPosition: "top" }} />
                        </div>
                        <span className="text-[10px] font-semibold text-gray-500 uppercase">Abhi · {(SITE_THEMES[selected.theme] || SITE_THEMES.default).label}</span>
                      </div>
                      <span className="text-2xl text-gray-400 pb-20">→</span>
                    </div>
                  )}
                  <PhoneShot src={draftTheme.previews.home} label="Home" />
                  <PhoneShot src={draftTheme.previews.login} label="Login" />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteThemePage;
